import type { Article } from '../types';
import { h2, p, ul, ol, callout, table } from '../b';

const article: Article = {
  slug: 'what-are-telegram-stars',
  title: 'What Are Telegram Stars? The Complete Guide for Users, Creators and Businesses',
  metaTitle: 'What Are Telegram Stars? Complete Guide (2026)',
  metaDescription:
    'Telegram Stars explained: what they are, how to buy them, what you can pay for, how creators and bots earn Stars, and how to convert Stars to TON or cash.',
  keywords: [
    'what are telegram stars',
    'telegram stars explained',
    'telegram stars meaning',
    'how do telegram stars work',
    'telegram stars currency',
    'buy telegram stars',
  ],
  category: 'Guides',
  datePublished: '2026-09-02',
  dateModified: '2026-10-10',
  readingTimeMin: 9,
  excerpt:
    'Telegram Stars are the in-app currency of Telegram. Users buy them to pay for digital goods inside bots, Mini Apps and channels; creators and businesses earn them and can convert them to TON or reinvest them in Telegram Ads. Here is everything you need to know.',
  keyTakeaways: [
    'Telegram Stars (currency code XTR) are a virtual currency introduced by Telegram in June 2024 for paying for digital goods and services inside Telegram.',
    'Users buy Stars through in-app purchases (App Store / Google Play), via the official @PremiumBot, or on Fragment with TON.',
    'Bots, Mini Apps and channels receive Stars when users pay; developers and creators can withdraw Stars to TON through Fragment or spend them on Telegram Ads.',
    'Stars are the only compliant way to sell digital content inside Telegram apps on iOS and Android, because of Apple and Google in-app purchase rules.',
    'Platforms like Telegram Stars Payment Platform let anyone accept Stars with a payment link, no coding required, for a flat 3% fee.',
  ],
  body: [
    h2('Telegram Stars in one paragraph'),
    p(
      'Telegram Stars are a virtual currency that lives inside Telegram. A user buys a pack of Stars with a credit card (through Apple or Google) or with the TON cryptocurrency, and then spends those Stars on digital products: a premium bot feature, an in-game item in a Mini App, a paid post in a channel, a gift for a friend. The seller, whether a bot developer, a channel owner or a merchant, receives the Stars in their balance and can later convert them to TON or use them to advertise on Telegram. In the Telegram Bot API, Stars are identified by the currency code **XTR**.',
    ),
    callout(
      'Looking for the fastest way to get paid in Stars? [Create a free account](/login) on Telegram Stars Payment Platform, generate a payment link in seconds and start receiving Stars with a 3% flat fee. See [how it works](/how-it-works).',
    ),

    h2('Why did Telegram create Stars?'),
    p(
      'Before Stars, bots could already accept payments through third-party payment providers such as Stripe. The problem was that Apple and Google require **digital goods sold inside iOS and Android apps** to go through their own in-app purchase systems, which take a 30% commission. That made it hard for Telegram to let developers sell digital content in a way that was compliant on mobile.',
    ),
    p(
      'Telegram Stars solve this. Users buy Stars through the official in-app purchase flow, so the store rules are respected, and developers simply price their goods in Stars. Telegram handles the compliance, the currency conversion and the payout rails. For the end user the experience is a native, two-tap checkout that works identically in every Telegram client.',
    ),

    h2('How do users buy Telegram Stars?'),
    p('There are three official ways to buy Stars, and the price per Star is not the same in each one:'),
    ul([
      '**In-app purchase** on iOS or Android: open any Stars payment, tap "Buy Stars" and pay with your App Store or Google Play account. This is the most convenient option but also the most expensive because of the store commission.',
      '**@PremiumBot** on Telegram: the official Telegram bot sells Stars packs and lets you gift Stars to other users.',
      '**Fragment** ([fragment.com/stars](https://fragment.com/stars)): Telegram\'s official marketplace lets you buy Stars with TON, typically at a lower price than the app stores because no store commission applies.',
    ]),
    p(
      'Stars packs usually range from 50 to several thousand Stars. Once bought, Stars sit in the user\'s Telegram account and can be spent anywhere in the ecosystem. Users cannot withdraw Stars to cash themselves; only the recipients of payments (developers, creators and businesses) can convert them.',
    ),

    h2('What can you pay for with Telegram Stars?'),
    p('The list keeps growing, but as of 2026 Stars are used for:'),
    ul([
      '**Bot payments**: any Telegram bot can issue an invoice priced in Stars using the Bot API. This is how digital subscriptions, courses, software licences, premium commands and consulting services are sold.',
      '**Mini App purchases**: games and web apps running inside Telegram sell items, upgrades and credits in Stars.',
      '**Paid media and paid posts in channels**: channel owners can lock photos and videos behind a Stars paywall.',
      '**Paid reactions and gifts**: users can send Stars to creators as reactions, or send collectible gifts purchased with Stars.',
      '**Channel subscriptions**: creators can offer monthly subscriptions priced in Stars with automatic renewal.',
      '**Paid messages**: some accounts and channels charge Stars for direct messages to reduce spam.',
    ]),

    h2('How do businesses and creators earn Stars?'),
    p(
      'Any Telegram bot can create a Stars invoice. The technical flow is: the bot calls `sendInvoice` or `createInvoiceLink` with `currency: "XTR"`, the user confirms the payment in Telegram, Telegram sends a `pre_checkout_query` that the bot must approve within 10 seconds, and finally a `successful_payment` update confirms the sale. Developers can read our [Bot API guide for Telegram Stars](/blog/telegram-stars-bot-api-developer-guide) for the full implementation.',
    ),
    p(
      'If you do not want to run your own bot, a Stars payment platform does the heavy lifting. With **Telegram Stars Payment Platform** you sign up, create a payment link with a title, description and amount in Stars, and share it. Payments are credited to your balance in real time, and you can withdraw to any TON wallet. Read [how to accept Telegram Stars payments](/blog/how-to-accept-telegram-stars-payments) for a step-by-step walkthrough.',
    ),

    h2('What is a Telegram Star worth?'),
    p(
      'There is no single fixed exchange rate because the purchase price depends on the channel (app store vs Fragment) and the pack size, while the payout value depends on the Fragment rate at withdrawal time. As a rule of thumb, users pay roughly 1.5 to 2.5 US cents per Star in-app, and developers historically received around 1.3 US cents per Star when converting to TON on Fragment. We keep a detailed, regularly updated breakdown in [How much is a Telegram Star worth?](/blog/telegram-stars-price-usd-ton).',
    ),
    table(
      ['', 'Who', 'Approximate value per Star'],
      [
        ['Buying in-app', 'End users', '≈ $0.015 – $0.025 (depends on pack and store)'],
        ['Buying on Fragment with TON', 'End users', 'Usually cheaper than in-app'],
        ['Converting to TON on Fragment', 'Developers / creators', 'Historically ≈ $0.013 (variable)'],
      ],
    ),

    h2('How do you convert Telegram Stars to TON or cash?'),
    p(
      'Telegram does not pay out Stars in fiat. Recipients have two options: withdraw Stars as **TON** cryptocurrency through Fragment (Stars must be at least 21 days old and withdrawals have a minimum amount), or spend Stars on **Telegram Ads** to promote a bot or channel. Once you hold TON you can sell it on any major exchange for dollars or euros.',
    ),
    p(
      'Platforms that aggregate payments simplify this further. Telegram Stars Payment Platform shows a maturing balance, applies the 21-day rule automatically and lets you request a payout to your TON address from the dashboard, with a minimum of only 100 Stars. The full process is described in [How to withdraw Telegram Stars to TON](/blog/withdraw-telegram-stars-to-ton).',
    ),

    h2('Telegram Stars vs other ways to get paid on Telegram'),
    p('Stars are not the only way to sell on Telegram, but they are the only one that works natively inside the apps on every platform:'),
    ul([
      '**Stars**: native checkout, mobile-compliant, Telegram handles taxes on the purchase side, payout in TON. Best for digital goods.',
      '**Third-party providers (Stripe, etc.)**: real fiat payouts and card payments, but only for physical goods or services and not usable for digital content on mobile.',
      '**Crypto wallets**: no intermediary, but a worse user experience and no built-in dispute handling.',
    ]),
    p('We compare these in depth in [Telegram Stars vs Stripe, PayPal and crypto](/blog/telegram-stars-vs-stripe-paypal-crypto).'),

    h2('Are Telegram Stars safe?'),
    p(
      'Purchases are processed by Apple, Google or Fragment, so sellers never see card details. Telegram enforces a holding period before Stars can be withdrawn, which protects against fraud and chargebacks, and bots can issue refunds in Stars at any time with `refundStarPayment`. For buyers the main thing to remember is that Stars are not transferable to cash and that refund policies are set by each seller.',
    ),

    h2('Who should use Telegram Stars?'),
    ol([
      '**Content creators** monetising a channel with paid posts, subscriptions or tips.',
      '**Bot and Mini App developers** selling premium features, credits or in-game items.',
      '**Educators and coaches** selling courses, PDFs, templates and consultations to a Telegram audience.',
      '**Communities** charging membership fees for private groups.',
      '**Small online businesses** that already sell to Telegram users and want a frictionless checkout.',
    ]),
    p('If you fall into any of these groups, the fastest path is to [create a Stars payment link](/blog/telegram-stars-payment-links) and share it with your audience today.'),
  ],
  faq: [
    {
      q: 'What are Telegram Stars used for?',
      a: 'Telegram Stars are used to pay for digital goods and services inside Telegram: bot features, Mini App purchases, paid channel posts, subscriptions, gifts and paid reactions.',
    },
    {
      q: 'Can you convert Telegram Stars to real money?',
      a: 'Users cannot cash out Stars. Developers, creators and merchants who receive Stars can withdraw them as TON cryptocurrency through Fragment (or a payment platform) and then sell TON for fiat on an exchange.',
    },
    {
      q: 'How much does one Telegram Star cost?',
      a: 'Roughly 1.5 to 2.5 US cents when bought in-app, depending on the pack size and the app store. Buying on Fragment with TON is usually cheaper.',
    },
    {
      q: 'Do I need to code to accept Telegram Stars?',
      a: 'No. With Telegram Stars Payment Platform you create a payment link from a dashboard, share it, and receive Stars in your balance. Developers who want full control can use the Bot API with currency XTR.',
    },
    {
      q: 'What is the currency code of Telegram Stars?',
      a: 'XTR. It is the value you pass in the currency field of sendInvoice and createInvoiceLink in the Telegram Bot API.',
    },
  ],
  relatedSlugs: [
    'how-to-accept-telegram-stars-payments',
    'telegram-stars-price-usd-ton',
    'withdraw-telegram-stars-to-ton',
  ],
};

export default article;
